import type Phaser from "phaser";
import type { AnimDir, AnimStateName } from "./types";
import { ArtPipelineCore } from "./pipeline-core";
import { bakeToDataUrl, ensurePhaserTexture, textureKey } from "./phaser-textures";

function animTextureKey(
  packId: string,
  spriteId: string,
  affiliationKey: string,
  state: AnimStateName,
  frameIndex: number,
  dir: AnimDir,
): string {
  return `art:${packId}:${spriteId}:${affiliationKey}:${dir}:${state}:${frameIndex}`;
}

/**
 * Full ArtPipeline API = core + Phaser/DOM adapters.
 */
export const ArtPipeline = {
  ...ArtPipelineCore,

  ensureSprite(
    scene: Phaser.Scene,
    packId: string,
    spriteId: string,
    affiliationKey: string,
  ): string {
    const key = textureKey(packId, spriteId, affiliationKey);
    if (scene.textures.exists(key)) return key;
    const baked = ArtPipelineCore.bake(packId, spriteId, affiliationKey);
    return ensurePhaserTexture(scene, key, baked);
  },

  ensureAnimFrame(
    scene: Phaser.Scene,
    packId: string,
    spriteId: string,
    affiliationKey: string,
    state: AnimStateName,
    timeSec: number,
    facing?: AnimDir,
  ): { key: string; frameIndex: number; clipDone: boolean; dir: AnimDir } {
    const { frameIndex, clipDone, dir } = ArtPipelineCore.resolveAnimFrame(
      packId,
      spriteId,
      state,
      timeSec,
      facing,
    );
    const key = animTextureKey(
      packId,
      spriteId,
      affiliationKey,
      state,
      frameIndex,
      dir,
    );
    if (scene.textures.exists(key)) {
      return { key, frameIndex, clipDone, dir };
    }
    const { baked } = ArtPipelineCore.bakeAnimFrame(
      packId,
      spriteId,
      affiliationKey,
      state,
      timeSec,
      facing,
    );
    ensurePhaserTexture(scene, key, baked);
    return { key, frameIndex, clipDone, dir };
  },

  spriteDataUrl(
    packId: string,
    spriteId: string,
    affiliationKey: string,
    scale = 2,
  ): string {
    return bakeToDataUrl(
      ArtPipelineCore.bake(packId, spriteId, affiliationKey),
      scale,
    );
  },
};
